import { DataCell } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { checkForDifferentValues } from '@folio/utils/checkForDifferentValues';
import { useQueries } from '@tanstack/react-query';
import {
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  type PolicyDto,
} from 'korint-sdk';
import type { CellType } from 'korint-sdk/react';
import _ from 'lodash';

type QuoteTierCustomFieldRowProps = {
  /**
   * The branches to compare.
   */
  branches: Array<string | undefined>;

  /**
   * The label of the row.
   */
  label: string;

  /**
   * The custom field path of the value.
   */
  fieldPath: string;

  /**
   * The type of the value. Will be passed to the DataCell component.
   */
  type: CellType;
};

export function QuoteTierCustomFieldRow({
  branches,
  label,
  fieldPath,
  type,
}: QuoteTierCustomFieldRowProps) {
  const { policyId } = useAssertedParams();

  const results = useQueries({
    queries: branches.map(branch => ({
      ...policyControllerGetPolicyQueryOptions(policyId, {
        branchId: branch,
      }),
      select: (d: PolicyDto) => {
        const data = { ...d, customFields: keyValueFieldsToObject(d.customFields ?? []) };

        return _.get(data, fieldPath) as string | number | undefined | null;
      },
    })),
  });

  const hasDifferentValues = checkForDifferentValues(results.map(result => result.data));

  return (
    <tr className={hasDifferentValues ? 'bg-slate-100' : ''}>
      <td className="font-semibold text-sm hidden md:table-cell">{label}</td>
      {results.map((result, index) => (
        <DataCell
          key={fieldPath + branches[index]}
          value={result.data}
          type={type}
          className="p-2"
        />
      ))}
    </tr>
  );
}
